import { Injectable } from '@angular/core';
import Keycloak, { KeycloakConfig, KeycloakLoginOptions } from 'keycloak-js';
import { environment } from '../../environments/environment';
import { SessionMonitorService } from '../security/session-monitor.service';
import { InitAppService } from './init-app.service';
import { FullSpinnerService } from './full-spinner.service';
import { StorageService } from './storage.service';

@Injectable({
    providedIn: 'root'
})
export class KeycloakService {
    private keycloak!: Keycloak;
    private initialized = false;

    constructor(
        private sessionMonitorService: SessionMonitorService,
        private initAppService: InitAppService,
        private fullSpinnerService: FullSpinnerService,
        private sessionStorage: StorageService) {
        const config: KeycloakConfig = {
            url: environment.loginUrl,
            realm: environment.loginRealm,
            clientId: environment.loginClientId,
        };
        this.keycloak = new Keycloak(config);
    }

    /**
     * Inizializza Keycloak all'avvio (APP_INITIALIZER)
     */
    async init(): Promise<boolean> {
        if (this.initialized) return this.keycloak.authenticated ?? false;

        const authenticated = await this.keycloak.init({
            onLoad: 'login-required',     // forza il login all'avvio
            checkLoginIframe: false,      // evita errori iframe in console
            pkceMethod: 'S256',           // consigliato per SPA moderne
        });
        if (authenticated) {
            this.sessionStorage.setToken(await this.getToken());
            this.initAppService.initApp().subscribe({
                next: (res) => { },
                complete: () => {
                    this.fullSpinnerService.spinner(undefined);
                },
                error: () => {
                    this.fullSpinnerService.spinner(undefined);
                },
            });
        }
        this.initialized = true;

        this.startTokenRefresh();

        return authenticated;
    }

    async isLoggedIn(): Promise<boolean> {
        return this.keycloak.authenticated ?? false;
    }

    async login(options?: KeycloakLoginOptions) {
        await this.keycloak.login(options);
    }

    async logout(redirectUri?: string) {
        await this.keycloak.logout({ redirectUri: redirectUri || window.location.origin });
        this.initAppService.logout();
    }

    async getToken(): Promise<string | undefined> {
        await this.updateToken();
        return this.keycloak.token;
    }

    private async updateToken(minValidity: number = 60) {
        if (!this.keycloak.token) return;

        try {
            const refreshed = await this.keycloak.updateToken(minValidity);
            if (refreshed) {
                 this.sessionStorage.setToken(await this.getToken());
            }
        } catch (err) {
            console.warn('Token non aggiornabile, forza login', err);
            await this.login({ redirectUri: window.location.href });
        }
    }

    /**
     * Avvia refresh automatico del token ogni minuto
     */
    private startTokenRefresh() {
        setInterval(() => {
            this.updateToken().catch(err => console.error(err));
        }, 60000);
    }


}
